import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import SeoHelmet from "../seo/SeoHelmet";
import {
  absoluteUrl,
  buildBlogPostingSchema,
  buildBreadcrumbSchema,
} from "../seo/schema";
import { blogPosts, getPostBySlug } from "../data/blogPosts";
import { blogCategories } from "../data/blogCategories";
import {
  estimateReadingMinutes,
  getPopularTags,
  getRelatedCategories,
  getRelatedPosts,
} from "./blogUtils";
import BlogSidebar from "./BlogSidebar";

function formatDate(dateISO) {
  const date = new Date(dateISO);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-PK", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function BlogPost() {
  const { slug } = useParams();

  const post = useMemo(() => getPostBySlug(slug), [slug]);
  const category = useMemo(
    () => (post ? blogCategories.find((item) => item.slug === post.categorySlug) : null),
    [post]
  );

  const relatedPosts = useMemo(
    () => (post ? getRelatedPosts(post, blogPosts, 6) : []),
    [post]
  );

  const relatedCategories = useMemo(() => {
    if (!post || relatedPosts.length >= 3) return [];
    return getRelatedCategories(post, blogCategories, blogPosts, 3);
  }, [post, relatedPosts.length]);

  const popularTags = useMemo(() => getPopularTags(blogPosts, 12), []);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const canonical = absoluteUrl(`/blog/${post.slug}`);
  const readingMinutes = post.readingMinutes || estimateReadingMinutes(post.content);

  const schema = [
    buildBlogPostingSchema(post, category),
    buildBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: category?.name || "Category", path: `/blog/category/${post.categorySlug}` },
      { name: post.title, path: `/blog/${post.slug}` },
    ]),
  ].filter(Boolean);

  return (
    <Container maxWidth="xl">
      <SeoHelmet
        title={`${post.title} | Cyberpunk Blog`}
        description={post.description}
        canonical={canonical}
        ogTitle={post.title}
        ogDescription={post.description}
        ogUrl={canonical}
        ogType="article"
        schema={schema}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            <Breadcrumbs aria-label="blog breadcrumb" sx={{ mb: 0.5 }}>
              <Link component={RouterLink} to="/" underline="hover" color="inherit">
                Home
              </Link>
              <Link component={RouterLink} to="/blog" underline="hover" color="inherit">
                Blog
              </Link>
              {category ? (
                <Link
                  component={RouterLink}
                  to={`/blog/category/${category.slug}`}
                  underline="hover"
                  color="inherit"
                >
                  {category.name}
                </Link>
              ) : null}
              <Typography color="text.primary">{post.title}</Typography>
            </Breadcrumbs>

            <Box>
              <Typography variant="h3" component="h1" sx={{ mb: 1 }}>
                {post.title}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5 }}>
                {category ? (
                  <Chip
                    label={category.name}
                    size="small"
                    component={RouterLink}
                    to={`/blog/category/${category.slug}`}
                    clickable
                    sx={{ textDecoration: "none" }}
                  />
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  Published: {formatDate(post.dateISO)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Updated: {formatDate(post.updatedISO || post.dateISO)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {readingMinutes} min read
                </Typography>
              </Stack>
              <Typography variant="body1" color="text.secondary">
                {post.description}
              </Typography>
            </Box>

            <Card variant="outlined">
              <CardContent>
                <Box
                  sx={{
                    "& h2": { mt: 3, mb: 1.2, fontSize: "1.55rem" },
                    "& h3": { mt: 2, mb: 1, fontSize: "1.2rem" },
                    "& p": { mb: 1.4, color: "text.secondary", lineHeight: 1.75 },
                    "& ul": { pl: 2.25, mb: 1.5 },
                    "& li": { mb: 0.7, color: "text.secondary" },
                    "& a": { color: "primary.main", textDecoration: "none" },
                    "& a:hover": { textDecoration: "underline" },
                  }}
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              </CardContent>
            </Card>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              {(post.tags || []).map((tag) => (
                <Chip
                  key={`${post.id}-${tag}`}
                  label={tag}
                  size="small"
                  component={RouterLink}
                  to={`/blog/tag/${encodeURIComponent(tag.toLowerCase())}`}
                  clickable
                  sx={{ textDecoration: "none" }}
                />
              ))}
            </Stack>

            <Link
              component={RouterLink}
              to={`/blog/category/${post.categorySlug}`}
              underline="hover"
              sx={{ alignSelf: "flex-start" }}
            >
              Back to category
            </Link>

            {relatedCategories.length ? (
              <Alert severity="info" variant="outlined">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Related Categories
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {relatedCategories.map((item) => (
                    <Chip
                      key={item.slug}
                      label={item.name}
                      size="small"
                      component={RouterLink}
                      to={`/blog/category/${item.slug}`}
                      clickable
                      sx={{ textDecoration: "none" }}
                    />
                  ))}
                </Stack>
              </Alert>
            ) : null}

            <Divider />
            <Typography variant="caption" color="text.secondary">
              For lawful investigation and compliance only.
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <BlogSidebar
            relatedPosts={relatedPosts}
            categories={blogCategories}
            popularTags={popularTags}
          />
        </Grid>
      </Grid>
    </Container>
  );
}

export default BlogPost;
